import { z } from 'zod';
import { restricted } from './redaction.js';
/**
 * Client and intake contracts (slice-01 §6, spec §6.3.2).
 *
 * ## The PHI fields are declared once and reused
 *
 * `ssnLast4`, `medicaidId` and `medicareId` are built by {@link phi} below and
 * the **same schema instance** goes into both the summary and the detail
 * shapes. That is deliberate and load-bearing.
 *
 * Redaction is keyed on the schema instance (see `redaction.ts`), so reusing
 * the instance means the list endpoint and the detail endpoint cannot disagree
 * about whether a field is sensitive. Declaring them separately — even with
 * identical code — is how a list endpoint ends up returning Medicaid IDs to a
 * caller who is correctly refused them on the detail page, which is the usual
 * shape of this bug: the list is where redaction is forgotten, because the list
 * is written first and reviewed as "just the names".
 */
export const genderSchema = z.enum(['MALE', 'FEMALE', 'NONBINARY', 'OTHER', 'UNDISCLOSED']);
export const clientStatusSchema = z.enum(['REFERRAL', 'INTAKE', 'ACTIVE', 'ON_HOLD', 'DISCHARGED']);
export const clientAddressTypeSchema = z.enum(['HOME', 'ALTERNATE', 'FACILITY', 'MAILING']);
export const geocodeAccuracySchema = z.enum([
    'ROOFTOP',
    'RANGE_INTERPOLATED',
    'APPROXIMATE',
    'FAILED',
]);
export const contactSharingLevelSchema = z.enum(['SCHEDULE_ONLY', 'SCHEDULE_AND_VISITS', 'FULL']);
export const clientPreferenceKeySchema = z.enum([
    'CAREGIVER_GENDER',
    'LANGUAGE',
    'SMOKING',
    'PETS',
    'DIETARY',
    'CULTURAL',
    'OTHER',
]);
export const transferAssistCountSchema = z.enum(['NONE', 'ONE_PERSON', 'TWO_PERSON']);
export const allergyCategorySchema = z.enum(['DRUG', 'FOOD', 'ENVIRONMENTAL', 'OTHER']);
export const allergySeveritySchema = z.enum(['MILD', 'MODERATE', 'SEVERE', 'ANAPHYLAXIS']);
/**
 * From the design partner's Comprehensive Assessment form: ADLs first, then
 * IADLs, in the order an assessor reads them out.
 */
export const activityOfDailyLivingSchema = z.enum([
    'BATHING',
    'PERSONAL_HYGIENE',
    'TOILETING',
    'DRESSING',
    'MOBILITY_TRANSFERS',
    'EATING_DRINKING',
    'MEDICATION_REMINDER',
    'MEAL_PREPARATION',
    'LIGHT_HOUSEKEEPING',
    'GROCERY_SHOPPING',
    'TRANSPORTATION',
    'LAUNDRY',
    'HANDLING_MONEY',
    'USING_TELEPHONE',
    'READING_ASSISTANCE',
    'WASH_EQUIPMENT',
]);
export const assistanceLevelSchema = z.enum(['DEPENDENT', 'INDEPENDENT', 'ASSIST', 'CUE']);
/**
 * A field only a caller holding `client:read_phi` may see.
 *
 * One helper rather than three `restricted(...)` calls, so the permission is
 * named once. Adding a fourth PHI field is a call to this function, and
 * forgetting the permission is not an available mistake.
 */
const phi = (schema) => restricted(schema, 'client:read_phi');
/**
 * The three identifiers that carry PHI on the client record itself.
 *
 * Built as a factory so every DTO gets the *same* instances — see the note at
 * the top of this file. `diagnosisCodes` belongs to `authorization` in §6.3.3
 * rather than to `client`; slice-01 §6 lists it under redaction and it is
 * wired there when that endpoint lands, from this same helper.
 */
const phiIdentifiers = {
    ssnLast4: phi(z.string().length(4).nullable()),
    medicaidId: phi(z.string().nullable()),
    medicareId: phi(z.string().nullable()),
};
/**
 * The row a list returns.
 *
 * Carries the PHI identifiers on purpose. It would be safer-looking to leave
 * them off the summary entirely — but an agency's roster export needs them and
 * a caller who holds `client:read_phi` is entitled to them, so the answer is to
 * redact rather than to omit. Omitting would also hide the bug this file is
 * written to prevent: a list that never carried the fields proves nothing about
 * a list that later does.
 */
export const clientSummarySchema = z.object({
    id: z.string().uuid(),
    mrn: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    preferredName: z.string().nullable(),
    dateOfBirth: z.string(),
    status: clientStatusSchema,
    statusEffectiveAt: z.string(),
    ...phiIdentifiers,
});
export const clientAddressSchema = z.object({
    id: z.string().uuid(),
    type: clientAddressTypeSchema,
    line1: z.string(),
    line2: z.string().nullable(),
    city: z.string(),
    state: z.string().length(2),
    postalCode: z.string(),
    country: z.string(),
    timezone: z.string().nullable(),
    geocodeAccuracy: geocodeAccuracySchema.nullable(),
    geofenceRadiusMeters: z.number().int().nullable(),
    entryInstructions: z.string().nullable(),
    phone: z.string().nullable(),
    evvVerifiedCallerIds: z.array(z.string()),
    isPrimary: z.boolean(),
});
export const clientContactSchema = z.object({
    id: z.string().uuid(),
    relationship: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    isEmergencyContact: z.boolean(),
    isResponsibleParty: z.boolean(),
    hasPortalAccess: z.boolean(),
    sharingLevel: contactSharingLevelSchema,
});
export const clientPreferenceSchema = z.object({
    id: z.string().uuid(),
    key: clientPreferenceKeySchema,
    value: z.string(),
    isHardRequirement: z.boolean(),
});
/**
 * An allergy a caregiver needs to know about on day one.
 *
 * The allergen is **free text**. `allergen_suggestion` offers the common ones
 * and constrains nothing: the ninth allergen a nurse needs to record arrives on
 * a Tuesday, and an enum would make that a migration and a deploy while the
 * client's documented allergy sat in a note. See ADR-013.
 */
export const clientAllergySchema = z.object({
    id: z.string().uuid(),
    allergen: z.string(),
    category: allergyCategorySchema,
    reaction: z.string().nullable(),
    severity: allergySeveritySchema.nullable(),
    notedAt: z.string(),
    notedBy: z.string().uuid(),
});
/**
 * A medication a caregiver may need to **remind** a client about.
 *
 * `dose` and `frequency` are free text, and that is a decision rather than an
 * omission — ADR-014 has the whole of it. In short: this is a reference list an
 * aide reads, not a medication administration record. Home care aides remind;
 * they do not administer. Structuring dose into a dosing model would imply a
 * clinical capability the product does not have and must not appear to have.
 */
export const clientMedicationSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    dose: z.string(),
    frequency: z.string(),
    prescriber: z.string().nullable(),
    purpose: z.string().nullable(),
    isActive: z.boolean(),
    notedAt: z.string(),
    notedBy: z.string().uuid(),
});
/**
 * One activity's rating.
 *
 * The scheduler reads these to decide who can be assigned and how long a visit
 * takes, which is why they are intake data rather than care-plan data (B4).
 */
export const clientAssistanceEntrySchema = z.object({
    id: z.string().uuid(),
    activity: activityOfDailyLivingSchema,
    level: assistanceLevelSchema,
    detail: z.string().nullable(),
    assessedAt: z.string(),
    assessedBy: z.string().uuid(),
});
/** The single-client read. Same PHI instances as the summary. */
export const clientDetailSchema = clientSummarySchema.extend({
    middleName: z.string().nullable(),
    gender: genderSchema.nullable(),
    primaryLanguage: z.string().nullable(),
    interpreterNeeded: z.boolean(),
    startOfCareDate: z.string().nullable(),
    dischargeDate: z.string().nullable(),
    dischargeReason: z.string().nullable(),
    referralSourceId: z.string().uuid().nullable(),
    defaultPayerId: z.string().uuid().nullable(),
    transferAssistCount: transferAssistCountSchema.nullable(),
    ambulationAid: z.string().nullable(),
    transferAid: z.string().nullable(),
    addresses: z.array(clientAddressSchema),
    contacts: z.array(clientContactSchema),
    preferences: z.array(clientPreferenceSchema),
    /*
     * PHI, restricted as whole collections rather than field by field.
     *
     * Every field of an allergy is disclosing: the allergen names a condition,
     * the reaction describes it, and even the count is informative — "this client
     * has four documented allergies" is a clinical fact. There is no subset of
     * these that is safe to return to someone refused the rest, so the array
     * carries the restriction and the caller sees no key at all.
     *
     * That is a different shape from `ssnLast4` and its neighbours, which sit on
     * a record whose other fields a coordinator legitimately needs. Same
     * mechanism, applied at the level the sensitivity actually lives at.
     */
    allergies: restricted(z.array(clientAllergySchema), 'client:read_phi'),
    medications: restricted(z.array(clientMedicationSchema), 'client:read_phi'),
    assistanceProfile: restricted(z.array(clientAssistanceEntrySchema), 'client:read_phi'),
});
// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 1942-03-11 — four digits, two, two.');
export const createClientSchema = z.object({
    mrn: z.string().trim().min(1).max(64),
    firstName: z.string().trim().min(1).max(120),
    middleName: z.string().trim().max(120).nullish(),
    lastName: z.string().trim().min(1).max(120),
    preferredName: z.string().trim().max(120).nullish(),
    dateOfBirth: isoDate,
    gender: genderSchema.nullish(),
    ssnLast4: z
        .string()
        .regex(/^\d{4}$/)
        .nullish(),
    medicaidId: z.string().trim().max(64).nullish(),
    medicareId: z.string().trim().max(64).nullish(),
    primaryLanguage: z.string().trim().max(8).nullish(),
    interpreterNeeded: z.boolean().default(false),
    referralSourceId: z.string().uuid().nullish(),
    defaultPayerId: z.string().uuid().nullish(),
    transferAssistCount: transferAssistCountSchema.nullish(),
    ambulationAid: z.string().trim().max(200).nullish(),
    transferAid: z.string().trim().max(200).nullish(),
});
/**
 * Everything except status, which moves only through
 * `POST /v1/clients/:id/status`.
 *
 * A PATCH that could set `status` would be a status change with no reason, no
 * actor and no history row — the three things §4 requires of every transition.
 * Leaving the field out of the schema is what makes that unavailable rather
 * than merely discouraged.
 */
export const updateClientSchema = createClientSchema.partial().strict();
/**
 * A status change, which is every move except one.
 *
 * `DISCHARGED` stays in the enum rather than being subtracted from it, and that
 * is deliberate. Subtracting it would make the refusal a schema error —
 * "expected one of REFERRAL, INTAKE, ACTIVE, ON_HOLD" — which tells a
 * coordinator that discharge is impossible rather than that it is elsewhere.
 * The route rejects it explicitly with `CLIENT_DISCHARGE_IS_ITS_OWN_ACTION`,
 * whose message says where to go instead.
 */
export const changeClientStatusSchema = z.object({
    toStatus: clientStatusSchema,
    reason: z.string().trim().min(1).max(2_000),
    /** Defaults to now. Backdating a correction is legitimate and audited. */
    effectiveAt: z.string().datetime().optional(),
});
/**
 * Discharge — a different act from a status change.
 *
 * It ends an episode of care and stops billing, which is why it has its own
 * route, its own permission and its own request shape. §10.4 grants
 * `client:discharge` separately from `client:update`, and a role that can
 * correct a client's name is not thereby a role that can end their care.
 *
 * `effectiveAt` is a **calendar date**, not an instant. A discharge takes
 * effect on a day, `discharge_date` is a `date` column, and sending a datetime
 * would make which day it lands on depend on the reader's zone.
 */
export const dischargeClientSchema = z.object({
    reason: z.string().trim().min(1).max(2_000),
    /**
     * Defaults to today. Backdating is legitimate and common — care usually stops
     * before anyone gets to the console to record it.
     */
    effectiveAt: isoDate.optional(),
});
export const clientStatusHistoryEntrySchema = z.object({
    id: z.string().uuid(),
    fromStatus: clientStatusSchema.nullable(),
    toStatus: clientStatusSchema,
    effectiveAt: z.string(),
    reason: z.string(),
    changedBy: z.string().uuid(),
    createdAt: z.string(),
});
export const clientStatusHistorySchema = z.object({
    entries: z.array(clientStatusHistoryEntrySchema),
});
export const createClientAddressSchema = z.object({
    type: clientAddressTypeSchema,
    line1: z.string().trim().min(1).max(200),
    line2: z.string().trim().max(200).nullish(),
    city: z.string().trim().min(1).max(120),
    state: z.string().trim().length(2),
    postalCode: z.string().trim().min(3).max(12),
    country: z.string().trim().length(2).default('US'),
    timezone: z.string().trim().max(64).nullish(),
    geofenceRadiusMeters: z.number().int().positive().max(5_000).nullish(),
    entryInstructions: z.string().trim().max(1_000).nullish(),
    phone: z.string().trim().max(32).nullish(),
    evvVerifiedCallerIds: z.array(z.string().trim().max(32)).default([]),
    isPrimary: z.boolean().default(false),
});
export const updateClientAddressSchema = createClientAddressSchema.partial().strict();
export const createClientContactSchema = z.object({
    relationship: z.string().trim().min(1).max(80),
    firstName: z.string().trim().min(1).max(120),
    lastName: z.string().trim().min(1).max(120),
    phone: z.string().trim().max(32).nullish(),
    email: z.string().trim().email().max(200).nullish(),
    isEmergencyContact: z.boolean().default(false),
    isResponsibleParty: z.boolean().default(false),
    sharingLevel: contactSharingLevelSchema.default('SCHEDULE_ONLY'),
});
export const updateClientContactSchema = createClientContactSchema.partial().strict();
/**
 * PUT, not PATCH: the whole set is replaced.
 *
 * Preferences are a set rather than a list of records a user edits one at a
 * time — the intake form shows all of them together and saves them together.
 * A PATCH would need a per-row id the form does not have.
 */
export const putClientPreferencesSchema = z.object({
    preferences: z
        .array(z.object({
        key: clientPreferenceKeySchema,
        value: z.string().trim().min(1).max(200),
        isHardRequirement: z.boolean().default(false),
    }))
        .max(50),
});
// ---------------------------------------------------------------------------
// Intake sets — allergies, medications, the assistance profile
// ---------------------------------------------------------------------------
//
// All three are PUT, replacing the whole set, for the same reason preferences
// are: the intake form shows them together and saves them together, and a PATCH
// would need a per-row id the form does not have.
export const putClientAllergiesSchema = z.object({
    allergies: z
        .array(z.object({
        /** Free text. `allergen_suggestion` offers; it does not constrain. */
        allergen: z.string().trim().min(1).max(200),
        category: allergyCategorySchema,
        reaction: z.string().trim().max(500).nullish(),
        severity: allergySeveritySchema.nullish(),
    }))
        .max(50),
});
export const putClientMedicationsSchema = z.object({
    medications: z
        .array(z.object({
        name: z.string().trim().min(1).max(200),
        /**
         * Free text, and deliberately so — ADR-014.
         *
         * "1 tablet", "half a tablet in the morning", "as needed for pain".
         * The last of those has no structured representation that is not a
         * lie, and inventing one would make the product look like it
         * understands a prescription. It does not, and must not appear to.
         */
        dose: z.string().trim().min(1).max(200),
        frequency: z.string().trim().min(1).max(200),
        prescriber: z.string().trim().max(200).nullish(),
        purpose: z.string().trim().max(500).nullish(),
        isActive: z.boolean().default(true),
    }))
        .max(100),
});
export const putClientAssistanceProfileSchema = z.object({
    /**
     * At most one rating per activity — a client cannot be both DEPENDENT and
     * INDEPENDENT for bathing, and the database agrees: there is a partial unique
     * index on (agency_id, client_id, activity).
     */
    entries: z
        .array(z.object({
        activity: activityOfDailyLivingSchema,
        level: assistanceLevelSchema,
        detail: z.string().trim().max(500).nullish(),
    }))
        .max(16)
        .superRefine((entries, ctx) => {
        const seen = new Set();
        for (const [index, entry] of entries.entries()) {
            if (seen.has(entry.activity)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [index, 'activity'],
                    message: `${entry.activity} is rated twice. Each activity gets one rating.`,
                });
            }
            seen.add(entry.activity);
        }
    }),
});
/** The seeded typeahead list. Not tenant data and not PHI. */
export const allergenSuggestionSchema = z.object({
    label: z.string(),
    category: allergyCategorySchema,
});
export const allergenSuggestionListSchema = z.object({
    suggestions: z.array(allergenSuggestionSchema),
});
export const clientAllergyListSchema = z.object({
    allergies: restricted(z.array(clientAllergySchema), 'client:read_phi'),
});
export const clientMedicationListSchema = z.object({
    medications: restricted(z.array(clientMedicationSchema), 'client:read_phi'),
});
export const clientAssistanceProfileListSchema = z.object({
    entries: restricted(z.array(clientAssistanceEntrySchema), 'client:read_phi'),
});
export const clientListQuerySchema = z.object({
    /** Matches MRN exactly, or name loosely. */
    search: z.string().trim().max(120).optional(),
    status: clientStatusSchema.optional(),
    /**
     * Matches a client covered by this payer, by rule or as the fall-through.
     *
     * Server-side because the roster is paged. A filter applied in the browser
     * can only narrow what has been loaded, so it would answer "who pays" for the
     * first hundred clients and quietly leave out the rest.
     */
    payerId: z.string().uuid().optional(),
    /**
     * The date the PAYER column is about. Defaults to today.
     *
     * Coverage lapses, so who pays is only answerable about a day. Shared with
     * `GET /v1/clients/:id/coverage`, and resolved through the same definition,
     * so the roster and the panel cannot disagree about a client on a date.
     */
    asOf: isoDate.optional(),
    /** 1-based. */
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(25),
});
/** A payer, named, as it appears beside a client in a list. */
export const clientPayerRefSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
});
/**
 * A row of the roster.
 *
 * The summary plus **who pays**, which is the one thing a coordinator reads the
 * list for that the summary did not carry. The column existed in the console
 * from the day the screen was built and answered "Not set" for every client in
 * a fully seeded database, because there was no field here for it to read.
 *
 * `payers` is an ordered list rather than a single name, and that is the whole
 * point of it: a client with two concurrent payers (§18.2) is a different
 * scheduling proposition from one with a single payer, and a lone
 * `primaryPayerName` cannot express the difference. The order matches
 * `GET /v1/clients/:id/coverage` — rules by priority, then the fall-through
 * default payer — so the roster and the coverage panel never disagree about
 * who pays first.
 */
export const clientListItemSchema = clientSummarySchema.extend({
    payers: z.array(clientPayerRefSchema),
});
export const clientListSchema = z.object({
    clients: z.array(clientListItemSchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
});
/**
 * The PHI field names, for tests and for the audit hasher.
 *
 * Derived from the schema rather than restated, so it cannot fall out of step
 * with what `restricted()` actually marked — which is the failure mode a second
 * hand-written list has.
 */
export const CLIENT_PHI_FIELDS = [
    ...Object.keys(phiIdentifiers),
    'allergies',
    'medications',
    'assistanceProfile',
];
//# sourceMappingURL=client.js.map